/**
 * Inputs: TMD CAP RSS/XML and trip stays.
 * Outputs: structured bulletins and whether one covers a current/imminent stay.
 *
 * Match on CAP areaDesc and polygons, never the weather-discussion prose.
 * An aviation notice is not a ground warning. A South rain cell is not Chiang Mai.
 */

import { DateTime } from "luxon";
import { CONFIRMED_HOTELS, isHotelPlaceId } from "./hotel-locations.ts";
import type { TripDocument } from "./schema.ts";
import { todayInZone } from "./time.ts";

export const TMD_CAP_RSS_URL = "https://www.tmd.go.th/en/api/xml/CAP";
export const TMD_WARNING_PAGE_URL = "https://www.tmd.go.th/en/warning-and-events/warning-storm";

export type CapPoint = { lat: number; lng: number };

export type CapBulletin = {
  id: string;
  headline: string;
  event: string;
  category: string;
  severity: string;
  urgency: string;
  instruction: string;
  description: string;
  areaDesc: string;
  effective: string | null;
  expires: string | null;
  link: string;
  polygons: CapPoint[][];
};

export type CapStayMatch = {
  stayId: string;
  chapter: string;
  href: string;
  how: "polygon" | "place";
};

const PLACE_ALIASES: Record<string, string[]> = {
  "Chiang Mai": ["chiang mai", "chiangmai", "hang dong", "lamphun", "mae rim"],
  "Hang Dong": ["hang dong", "chiang mai", "chiangmai", "lamphun"],
  "Koh Samet": ["samet", "samed", "rayong", "klaeng", "ban phe", "chon buri", "chonburi"],
  Bangkok: ["bangkok", "krung thep", "bang rak", "silom", "samut prakan", "samut prakarn"],
};

function decode(raw: string): string {
  return raw
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, "$1")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function tagText(xml: string, tag: string): string {
  const match = new RegExp(`<(?:[\\w.]+:)?${tag}(?:\\s[^>]*)?>([\\s\\S]*?)</(?:[\\w.]+:)?${tag}>`, "i").exec(xml);
  return match ? decode(match[1].replace(/<[^>]+>/g, " ")) : "";
}

function allTagTexts(xml: string, tag: string): string[] {
  const out: string[] = [];
  const re = new RegExp(`<(?:[\\w.]+:)?${tag}(?:\\s[^>]*)?>([\\s\\S]*?)</(?:[\\w.]+:)?${tag}>`, "gi");
  for (const match of xml.matchAll(re)) {
    const text = decode(match[1].replace(/<[^>]+>/g, " "));
    if (text) out.push(text);
  }
  return out;
}

export function parseCapPolygons(areaXml: string): CapPoint[][] {
  const out: CapPoint[][] = [];
  for (const raw of allTagTexts(areaXml, "polygon")) {
    const ring: CapPoint[] = [];
    for (const pair of raw.split(/\s+/)) {
      const [latRaw, lngRaw] = pair.split(",");
      const lat = Number(latRaw);
      const lng = Number(lngRaw);
      if (Number.isFinite(lat) && Number.isFinite(lng)) ring.push({ lat, lng });
    }
    if (ring.length >= 3) out.push(ring);
  }
  return out;
}

export function pointInPolygon(point: CapPoint, ring: CapPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const intersect = (a.lng > point.lng) !== (b.lng > point.lng)
      && point.lat < ((b.lat - a.lat) * (point.lng - a.lng)) / (b.lng - a.lng + 1e-12) + a.lat;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function parseCapBulletin(xml: string, link = ""): CapBulletin | null {
  if (!/<alert[\s>]/i.test(xml) && !/emergency:cap/i.test(xml)) return null;
  const info = /<(?:[\w.]+:)?info[\s>][\s\S]*$/i.exec(xml)?.[0] ?? xml;
  const area = /<(?:[\w.]+:)?area[\s>][\s\S]*$/i.exec(info)?.[0] ?? info;
  const category = tagText(info, "category");
  const event = tagText(info, "event") || tagText(info, "headline");
  const id = tagText(xml, "identifier") || link;
  if (!id && !event) return null;
  return {
    id: id || `${event}:${tagText(xml, "sent")}`,
    headline: tagText(info, "headline") || event,
    event,
    category,
    severity: tagText(info, "severity"),
    urgency: tagText(info, "urgency"),
    instruction: tagText(info, "instruction"),
    description: tagText(info, "description"),
    areaDesc: tagText(area, "areaDesc"),
    effective: tagText(info, "effective") || tagText(xml, "sent") || null,
    expires: tagText(info, "expires") || null,
    link,
    polygons: parseCapPolygons(area),
  };
}

export function parseCapRssItems(xml: string): Array<{ title: string; summary: string; link: string; guid: string }> {
  const blocks = [...xml.matchAll(/<(?:item|entry)[\s\S]*?<\/(?:item|entry)>/gi)].map((row) => row[0]);
  const out: Array<{ title: string; summary: string; link: string; guid: string }> = [];
  for (const block of blocks.slice(0, 12)) {
    const title = tagText(block, "title");
    const link = tagText(block, "link") || /<link[^>]*href=["']([^"']+)["']/i.exec(block)?.[1] || "";
    if (!title && !link) continue;
    out.push({
      title,
      summary: tagText(block, "description"),
      link,
      guid: tagText(block, "guid") || link,
    });
  }
  return out;
}

export function isAviationBulletin(bulletin: CapBulletin): boolean {
  const hay = `${bulletin.category} ${bulletin.event} ${bulletin.headline}`;
  return /\baviation\b|\bsigmet\b|\bairmet\b/i.test(hay);
}

export function bulletinIsActive(bulletin: CapBulletin, now: Date): boolean {
  const ms = now.getTime();
  if (bulletin.expires) {
    const expires = Date.parse(bulletin.expires);
    if (Number.isFinite(expires) && ms >= expires) return false;
  }
  if (bulletin.effective) {
    const effective = Date.parse(bulletin.effective);
    if (Number.isFinite(effective) && ms + 30 * 60 * 1000 < effective) return false;
  }
  return true;
}

function areaHay(areaDesc: string): string {
  return areaDesc.toLowerCase().replace(/[-_]/g, " ");
}

function placeAliasHit(areaDesc: string, chapter: string): boolean {
  const hay = areaHay(areaDesc);
  return (PLACE_ALIASES[chapter] ?? []).some((alias) => hay.includes(alias));
}

function regionHit(areaDesc: string, chapter: string): boolean {
  const hay = areaHay(areaDesc);
  if (/\bnorth-?east|northeastern\b/.test(hay)) {
    return false;
  }
  if (/\bnorth(?:ern)?\b/.test(hay) && (chapter === "Chiang Mai" || chapter === "Hang Dong")) return true;
  if (/\beast(?:ern)?\b/.test(hay) && chapter === "Koh Samet") return true;
  if (/\bcentral\b|\bbangkok and vicinity\b/.test(hay) && chapter === "Bangkok") return true;
  return false;
}

export function stayCoords(doc: TripDocument, placeId: string): CapPoint | null {
  const place = doc.places.find((row) => row.id === placeId);
  if (place?.coordinates && Number.isFinite(place.coordinates.lat) && Number.isFinite(place.coordinates.lng)) {
    return { lat: place.coordinates.lat, lng: place.coordinates.lng };
  }
  if (isHotelPlaceId(placeId)) {
    const hotel = CONFIRMED_HOTELS[placeId];
    return { lat: hotel.lat, lng: hotel.lng };
  }
  return null;
}

function stayIsCurrentOrImminent(doc: TripDocument, stay: TripDocument["stays"][number], now: Date): boolean {
  const zone = stay.timezone || doc.trip.diaryTimezone;
  const date = todayInZone(now, zone);
  if (stay.startDate <= date && date < stay.endDate) return true;
  const start = DateTime.fromISO(stay.startDate, { zone }).startOf("day");
  if (!start.isValid) return false;
  const hours = start.diff(DateTime.fromJSDate(now, { zone }), "hours").hours;
  return hours >= 0 && hours <= 48;
}

export function matchCapToStay(doc: TripDocument, bulletin: CapBulletin, now: Date): CapStayMatch | null {
  if (isAviationBulletin(bulletin) || !bulletinIsActive(bulletin, now)) return null;
  const date = todayInZone(now, doc.trip.diaryTimezone);
  const ordered = [...doc.stays].sort((a, b) => a.startDate.localeCompare(b.startDate));
  for (const stay of ordered) {
    if (!stayIsCurrentOrImminent(doc, stay, now)) continue;
    const coords = stayCoords(doc, stay.placeId);
    const polygonHit = Boolean(coords && bulletin.polygons.some((ring) => pointInPolygon(coords, ring)));
    const named = placeAliasHit(bulletin.areaDesc, stay.chapter);
    const region = bulletin.polygons.length === 0 && regionHit(bulletin.areaDesc, stay.chapter);
    if (polygonHit) {
      return {
        stayId: stay.id,
        chapter: stay.chapter,
        href: `/journey/day/${date < stay.startDate ? stay.startDate : date}`,
        how: "polygon",
      };
    }
    if (named || region) {
      return { stayId: stay.id, chapter: stay.chapter, href: `/journey/stay/${stay.id}`, how: "place" };
    }
  }
  return null;
}

export function bulletinCoversStayPlace(doc: TripDocument, bulletin: CapBulletin): boolean {
  for (const stay of doc.stays) {
    const coords = stayCoords(doc, stay.placeId);
    if (coords && bulletin.polygons.some((ring) => pointInPolygon(coords, ring))) return true;
    if (placeAliasHit(bulletin.areaDesc, stay.chapter)) return true;
    if (bulletin.polygons.length === 0 && regionHit(bulletin.areaDesc, stay.chapter)) return true;
  }
  return false;
}

export function bulletinIsElsewhere(doc: TripDocument, bulletin: CapBulletin): boolean {
  if (!bulletin.areaDesc && !bulletin.polygons.length) return false;
  return !bulletinCoversStayPlace(doc, bulletin);
}
